import { useEffect, useRef, useState } from 'react';
import useAuthenticatedApi from '@/src/Authentication/components/hooks/useAuthenticatedApi';
import { toast } from 'sonner';
import useUserOrg from '@/src/Authentication/components/hooks/useUserOrg';
import { useNavigate } from 'react-router-dom';
import {
    EXPERIENCE_TEMPLATE_TYPE,
    PROCESSING_FLOW_TEMPLATE_TYPE,
} from '../constants';

const initialSteps = [
    {
        id: 'Step 1',
        name: 'Event Details',
        status: 'current',
    },
    {
        id: 'Step 2',
        name: 'Experience & Creative',
        status: 'upcoming',
    },
    {
        id: 'Step 3',
        name: 'Sharing',
        status: 'upcoming',
    },
];

export interface DefinitionTemplate {
    name: string;
    description: string;
    demo?: string;
    definitionJsonString: string;
}

export default function useCreateEvent() {
    const navigate = useNavigate();
    const authenticatedApi = useAuthenticatedApi();
    const { orgId } = useUserOrg();
    const [brandDefaults, setBrandDefaults] = useState<any>(null);
    const [isLoading, setIsLoading] = useState(true);

    const isCreatingEvent = useRef(false);

    const [steps, setSteps] = useState(initialSteps);
    const [platform, setPlatform] = useState<null | 'romr' | 'clickerbooth'>(
        null,
    );
    const [experience, setExperience] = useState<DefinitionTemplate | null>(
        null,
    );

    const [experienceTemplates, setExperienceTemplates] = useState<
        DefinitionTemplate[]
    >([]);

    const [processingFlowTemplates, setProcessingFlowTemplates] = useState<
        DefinitionTemplate[]
    >([]);

    const [shareViaText, setShareViaText] = useState(true);
    const [shareViaEmail, setShareViaEmail] = useState(true);
    const [customMessage, setCustomMessage] = useState('');
    const [eventName, setEventName] = useState('');
    const [eventDate, setEventDate] = useState<Date>(new Date());
    const [eventLocation, setEventLocation] = useState<string | null>(null);
    const [eventTemplate, setEventTemplate] =
        useState<DefinitionTemplate | null>(null);
    const [processingFlow, setProcessingFlow] =
        useState<DefinitionTemplate | null>(null);
    const [processingFlowTemplate, setProcessingFlowTemplate] =
        useState<DefinitionTemplate | null>(null);
    const [galleryLogo, setGalleryLogo] = useState<string | null>(null);
    const [foregroundColor, setForegroundColor] = useState<string>('#000000');
    const [backgroundColor, setBackgroundColor] = useState<string>('#FFFFFF');
    const [headerForegroundColor, setHeaderForegroundColor] =
        useState<string>('#FFFFFF');
    const [headerBackgroundColor, setHeaderBackgroundColor] =
        useState<string>('#000000');

    const [templateValues, setTemplateValues] = useState<Record<string, any>>(
        {},
    );

    useEffect(() => {
        const loadData = async () => {
            setIsLoading(true);
            const response = await authenticatedApi.get(
                `/${orgId}/experience-templates`,
            );
            const templates = response.data;
            const experiences = templates.filter(
                (template: any) =>
                    template.templateType === EXPERIENCE_TEMPLATE_TYPE,
            );
            const processingFlows = templates.filter(
                (template: any) =>
                    template.templateType === PROCESSING_FLOW_TEMPLATE_TYPE,
            );

            const brandDefaultsResponse = await authenticatedApi.get(
                `/${orgId}/activations/v2/brandDefaults`,
            );

            setBrandDefaults(brandDefaultsResponse.data);
            setExperienceTemplates(experiences);
            setProcessingFlowTemplates(processingFlows);
            setIsLoading(false);
        };
        loadData();

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (brandDefaults) {
            setGalleryLogo(brandDefaults.gallerySettings.logo);
            setForegroundColor(brandDefaults.gallerySettings.foregroundColor);
            setBackgroundColor(brandDefaults.gallerySettings.backgroundColor);
            setHeaderForegroundColor(
                brandDefaults.gallerySettings.headerForegroundColor,
            );
            setHeaderBackgroundColor(
                brandDefaults.gallerySettings.headerBackgroundColor,
            );
            setShareViaText(brandDefaults.sharing.shareViaText);
            setShareViaEmail(brandDefaults.sharing.shareViaEmail);
            setCustomMessage(brandDefaults.sharing.customMessage);
        }
    }, [brandDefaults]);

    const recordValues = (
        values: Record<
            string,
            string | Date | boolean | DefinitionTemplate | null | number
        >,
    ) => {
        console.log('Recording values', values);
        for (const [key, value] of Object.entries(values)) {
            if (key === 'galleryLogo') {
                setGalleryLogo(value as string);
            } else if (key === 'eventName') {
                setEventName(value as string);
            } else if (key === 'eventDate') {
                setEventDate(new Date(value as string));
            } else if (key === 'eventLocation') {
                setEventLocation(value as string);
            } else if (key === 'shareViaText') {
                setShareViaText(value as boolean);
            } else if (key === 'shareViaEmail') {
                setShareViaEmail(value as boolean);
            } else if (key === 'customMessage') {
                setCustomMessage(value as string);
            } else if (key === 'eventTemplate') {
                setEventTemplate(value as unknown as DefinitionTemplate);
            } else if (key === 'experience') {
                setExperience(value as DefinitionTemplate);
            } else if (key === 'processingFlow') {
                setProcessingFlow(value as DefinitionTemplate);
            } else if (key === 'processingFlowTemplate') {
                setProcessingFlowTemplate(value as DefinitionTemplate);
            } else if (key === 'foregroundColor') {
                setForegroundColor(value as string);
            } else if (key === 'backgroundColor') {
                setBackgroundColor(value as string);
            } else if (key === 'headerForegroundColor') {
                setHeaderForegroundColor(value as string);
            } else if (key === 'headerBackgroundColor') {
                setHeaderBackgroundColor(value as string);
            }
        }
    };

    const goToStep = (stepId: string) => {
        const newStepIndex = steps.findIndex((step) => step.id === stepId);
        setSteps((prevSteps) =>
            prevSteps.map((step, index) =>
                index === newStepIndex
                    ? { ...step, status: 'current' }
                    : {
                          ...step,
                          status:
                              index < newStepIndex ? 'complete' : 'upcoming',
                      },
            ),
        );
    };

    const saveEvent = async () => {
        if (isCreatingEvent.current) {
            return;
        }
        isCreatingEvent.current = true;
        const experienceDefinition = eventTemplate;
        const processingFlowDefinition = processingFlow;

        const requestBody = {
            details: {
                name: eventName,
                date: eventDate,
                location: eventLocation,
            },
            experience: {
                experience: experienceDefinition,
                processingFlow: processingFlowDefinition,
            },
            sharing: {
                shareViaText: shareViaText,
                shareViaEmail: shareViaEmail,
                customMessage: customMessage,
            },
            gallerySettings: {
                logo: galleryLogo,
                name: eventName,
                foregroundColor: foregroundColor,
                backgroundColor: backgroundColor,
                headerForegroundColor: headerForegroundColor,
                headerBackgroundColor: headerBackgroundColor,
            },
        };

        console.log('requestBody', requestBody);

        try {
            const response = await authenticatedApi.post(
                `/${orgId}/activations/v2/create`,
                requestBody,
            );

            navigate(`/events/${response.data.id}`);
            toast.success('Event created successfully');
        } catch (error) {
            console.error('Error saving event', error);
            toast.error('Error saving event');
        } finally {
            isCreatingEvent.current = false;
        }
    };

    return {
        isLoading,
        steps,
        goToStep,
        platform,
        setPlatform,
        experience,
        setExperience,
        experienceTemplates,
        recordValues,
        saveEvent,
        processingFlow,
        setProcessingFlow,
        processingFlowTemplates,
        foregroundColor,
        setForegroundColor,
        backgroundColor,
        setBackgroundColor,
        headerForegroundColor,
        setHeaderForegroundColor,
        headerBackgroundColor,
        setHeaderBackgroundColor,
        customMessage,
        setCustomMessage,
        shareViaText,
        setShareViaText,
        shareViaEmail,
        setShareViaEmail,
        galleryLogo,
        setGalleryLogo,
        templateValues,
        setTemplateValues,
        processingFlowTemplate,
        setProcessingFlowTemplate,
        eventName,
        setEventName,
        eventDate,
        setEventDate,
        eventLocation,
        setEventLocation,
    };
}
